import React, { useState } from "react";
import { api } from "../lib/api";
import { refreshLookups, useLookups } from "../lib/lookups";
import { Plus, ChevronUp, ChevronDown, Trash2, Save, X } from "lucide-react";
import { toast } from "sonner";

// kind: "category" | "stage" | "course"
export default function LookupManager({ kind, title, description, withColor = false }) {
  const lookups = useLookups();
  const items =
    kind === "category" ? lookups.categories : kind === "stage" ? lookups.stages : lookups.courses;

  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#6C47FF");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = () => refreshLookups();

  const create = async () => {
    if (!newName.trim()) return toast.error("Enter a name");
    setBusy(true);
    try {
      const body = { name: newName.trim() };
      if (withColor) body.color = newColor;
      await api.post(`/lookups/${kind}`, body);
      toast.success(`${title.slice(0, -1)} added`);
      setNewName("");
      setNewColor("#6C47FF");
      setAdding(false);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (it) => {
    setEditId(it.id);
    setEditName(it.name);
    setEditColor(it.color || "#6C47FF");
  };

  const saveEdit = async (id) => {
    if (!editName.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      const body = { name: editName.trim() };
      if (withColor) body.color = editColor;
      await api.patch(`/lookups/${kind}/${id}`, body);
      toast.success("Updated");
      setEditId(null);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const move = async (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    const reordered = [...items];
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(newIdx, 0, moved);
    setBusy(true);
    try {
      await api.post(`/lookups/${kind}/reorder`, { ids: reordered.map((x) => x.id) });
      await refresh();
    } catch (e) {
      toast.error("Reorder failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (it) => {
    const ok = window.confirm(`Delete "${it.name}"? If any leads use it you'll be prompted to re-assign them.`);
    if (!ok) return;
    setBusy(true);
    try {
      await api.delete(`/lookups/${kind}/${it.id}`);
      toast.success("Deleted");
      await refresh();
    } catch (e) {
      const detail = e.response?.data?.detail || "Failed";
      if (detail.includes("leads use")) {
        const alt = window.prompt(`${detail}\n\nEnter the name of an existing ${kind} to move them to:`);
        if (!alt) {
          setBusy(false);
          return;
        }
        try {
          await api.delete(`/lookups/${kind}/${it.id}?move_to=${encodeURIComponent(alt)}`);
          toast.success(`Deleted. Leads moved to ${alt}.`);
          await refresh();
        } catch (e2) {
          toast.error(e2.response?.data?.detail || "Failed");
        }
      } else {
        toast.error(detail);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`lookup-${kind}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-sm font-[600]">{title}</div>
          {description && <div className="text-xs text-[#6B7280]">{description}</div>}
        </div>
        {!adding && (
          <button
            onClick={() => setAdding(true)}
            data-testid={`lookup-add-${kind}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-[6px] border border-[#6C47FF] text-xs font-[600] min-h-[36px]"
          >
            <Plus className="h-3 w-3" /> Add
          </button>
        )}
      </div>

      {adding && (
        <div className="flex items-center gap-2 mb-3 p-2 border border-[#E8EAEF] rounded-[8px] bg-[#F0F1F7]">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Name"
            data-testid={`lookup-newname-${kind}`}
            className="flex-1 rounded-[6px] border border-[#E8EAEF] px-2 py-1.5 text-sm bg-white"
          />
          {withColor && (
            <input
              type="color"
              value={newColor}
              onChange={(e) => setNewColor(e.target.value)}
              data-testid={`lookup-newcolor-${kind}`}
              className="h-9 w-12 rounded-[6px] border border-[#E8EAEF]"
            />
          )}
          <button onClick={create} disabled={busy} data-testid={`lookup-create-${kind}`} className="px-3 py-1.5 rounded-[6px] btn-mx text-xs font-[600]">
            <Save className="h-3 w-3" />
          </button>
          <button onClick={() => { setAdding(false); setNewName(""); }} className="px-2 py-1.5 rounded-[6px] border border-[#E8EAEF] text-xs">
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      <div className="space-y-1">
        {items.map((it, idx) => (
          <div key={it.id} className="flex items-center gap-2 p-2 border border-[#E8EAEF] rounded-[8px] bg-white" data-testid={`lookup-row-${kind}-${it.id}`}>
            <div className="flex flex-col">
              <button disabled={idx === 0 || busy} onClick={() => move(idx, -1)} className="p-0.5 disabled:opacity-30 text-[#6B7280]" data-testid={`lookup-up-${kind}-${it.id}`}>
                <ChevronUp className="h-3 w-3" />
              </button>
              <button disabled={idx === items.length - 1 || busy} onClick={() => move(idx, 1)} className="p-0.5 disabled:opacity-30 text-[#6B7280]" data-testid={`lookup-down-${kind}-${it.id}`}>
                <ChevronDown className="h-3 w-3" />
              </button>
            </div>
            {editId === it.id ? (
              <>
                <input value={editName} onChange={(e) => setEditName(e.target.value)} data-testid={`lookup-editname-${kind}-${it.id}`} className="flex-1 rounded-[6px] border border-[#E8EAEF] px-2 py-1 text-sm bg-white" />
                {withColor && (
                  <input type="color" value={editColor} onChange={(e) => setEditColor(e.target.value)} data-testid={`lookup-editcolor-${kind}-${it.id}`} className="h-8 w-10 rounded-[6px] border border-[#E8EAEF]" />
                )}
                <button onClick={() => saveEdit(it.id)} disabled={busy} data-testid={`lookup-save-${kind}-${it.id}`} className="px-2 py-1 rounded-[6px] bg-[#166534] text-white text-xs font-[600]">
                  <Save className="h-3 w-3" />
                </button>
                <button onClick={() => setEditId(null)} className="px-2 py-1 rounded-[6px] border border-[#E8EAEF] text-xs">
                  <X className="h-3 w-3" />
                </button>
              </>
            ) : (
              <>
                {withColor && (
                  <span className="inline-block h-6 w-6 rounded-full border border-[#E8EAEF]" style={{ backgroundColor: it.color }} />
                )}
                <span className="flex-1 text-sm font-[600] cursor-pointer" onClick={() => startEdit(it)}>{it.name}</span>
                <button onClick={() => startEdit(it)} data-testid={`lookup-edit-${kind}-${it.id}`} className="text-xs px-2 py-1 rounded-[6px] border border-[#6C47FF] font-[600]">Edit</button>
                <button onClick={() => remove(it)} disabled={busy} data-testid={`lookup-delete-${kind}-${it.id}`} className="text-xs px-2 py-1 rounded-[6px] border border-[#7f1d1d] text-[#7f1d1d] font-[600]">
                  <Trash2 className="h-3 w-3" />
                </button>
              </>
            )}
          </div>
        ))}
        {items.length === 0 && <div className="text-xs text-[#6B7280] text-center py-3">None yet.</div>}
      </div>
    </div>
  );
}
